import { useState, useEffect } from 'react'
import { useAuth } from '../context/auth'
import { useNavigate } from 'react-router-dom'
import { supabase, TowJob, Profile, BusinessUser } from '../lib/supabase'
import { createTeamMember } from '../lib/teamService'
import { Modal, TeamMemberSuccessModal, ErrorModal, ConfirmModal } from '../components/Modal'
import { hasRole, getAllRoles } from '../lib/roleUtils'
import { UserRole } from '../types'
import {
  LayoutDashboard, Wrench, Settings, Plus, MessageSquare,
  DollarSign, CreditCard, CheckCircle, Clock, TrendingUp, BarChart3,
  Activity, LogOut, Users, Truck, FileText, Star, Bell, Search,
  Filter, MoreVertical, Edit, Trash2, ArrowRight, Navigation, Menu, X, User, ChevronDown, BarChart, Package, Building2, BadgeCheck
} from 'lucide-react'

type Tab = 'overview' | 'jobs' | 'earnings' | 'settings' | 'reports' | 'team'

export default function GarageDashboard() {
  const { profile, signOut } = useAuth()
  const navigate = useNavigate()
  const [tab, setTab] = useState<Tab>('overview')
  const [jobs, setJobs] = useState<TowJob[]>([])
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  
  // Team management state
  const [teamMembers, setTeamMembers] = useState<any[]>([])
  const [showAddTeamMemberModal, setShowAddTeamMemberModal] = useState(false)
  const [newTeamMember, setNewTeamMember] = useState({
    email: '',
    full_name: '',
    role: 'staff' as 'admin' | 'staff'
  })
  const [addingTeamMember, setAddingTeamMember] = useState(false)
  const [showTeamSuccessModal, setShowTeamSuccessModal] = useState(false)
  const [teamSuccessModalData, setTeamSuccessModalData] = useState({
    email: '',
    fullName: '',
    tempPassword: '',
    role: '',
    businessName: '',
    emailSent: false
  })
  const [showTeamErrorModal, setShowTeamErrorModal] = useState(false)
  const [teamErrorModalData, setTeamErrorModalData] = useState({
    title: '',
    message: ''
  })
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [confirmModalData, setConfirmModalData] = useState({
    title: '',
    message: '',
    onConfirm: () => {}
  })
  const [showSwitchRoleModal, setShowSwitchRoleModal] = useState(false)

  const userRoles = getAllRoles(profile)

  const availableRoles: { value: UserRole; label: string; icon: any; description: string }[] = [
    { value: 'tower', label: 'Tower Services', icon: Truck, description: 'Provide towing and breakdown services' },
    { value: 'parts_vendor', label: 'Parts Sales', icon: Package, description: 'Sell vehicle parts and accessories' },
    { value: 'insurer', label: 'Insurance Provider', icon: Building2, description: 'Provide vehicle insurance services' },
  ]

  useEffect(() => {
    if (!profile) return
    supabase.from('tow_jobs').select('*').order('created_at', { ascending: false }).then(({ data }) => setJobs((data as TowJob[]) || []))
    
    // Load team members
    loadTeamMembers()
  }, [profile])

  const loadTeamMembers = async () => {
    if (!profile) return
    const { data } = await supabase
      .from('business_users')
      .select('*')
      .eq('business_id', profile.id)
    
    // Fetch profiles for each team member
    const userIds = (data || []).map(m => m.user_id)
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, full_name, email, phone')
      .in('id', userIds)
    
    const profilesMap = (profiles || []).reduce((acc, p) => {
      acc[p.id] = p
      return acc
    }, {} as Record<string, any>)
    
    const teamMembersWithProfiles = (data || []).map(m => ({
      ...m,
      profiles: profilesMap[m.user_id] || null
    }))
    
    setTeamMembers(teamMembersWithProfiles)
  }

  const addTeamMember = async () => {
    if (!profile) return
    setAddingTeamMember(true)
    
    try {
      // Call server-side API to create team member (prevents session switching)
      const result = await createTeamMember({
        email: newTeamMember.email,
        fullName: newTeamMember.full_name,
        role: newTeamMember.role,
        businessId: profile.id,
        businessName: profile?.business_name || profile?.full_name || 'Your Business',
        businessType: 'garage'
      })
      
      if (!result.success) {
        setTeamErrorModalData({
          title: 'Team Member Creation Failed',
          message: result.message
        })
        setShowTeamErrorModal(true)
        setAddingTeamMember(false)
        return
      }
      
      // Refresh team members
      await loadTeamMembers()
      
      // Reset form and close modal
      setNewTeamMember({ email: '', full_name: '', role: 'staff' })
      setShowAddTeamMemberModal(false)
      
      // Show success modal
      setTeamSuccessModalData({
        email: newTeamMember.email,
        fullName: newTeamMember.full_name,
        tempPassword: result.tempPassword || '',
        role: newTeamMember.role,
        businessName: profile?.business_name || profile?.full_name || 'Your Business',
        emailSent: result.emailSent
      })
      setShowTeamSuccessModal(true)
      
    } catch (error) {
      setTeamErrorModalData({
        title: 'Error Occurred',
        message: `An unexpected error occurred: ${(error as any).message}`
      })
      setShowTeamErrorModal(true)
    } finally {
      setAddingTeamMember(false)
    }
  }

  const removeTeamMember = async (userId: string) => {
    if (!profile) return
    
    setConfirmModalData({
      title: 'Remove Team Member',
      message: 'Are you sure you want to remove this team member? This action cannot be undone.',
      onConfirm: async () => {
        const { error } = await supabase
          .from('business_users')
          .delete()
          .eq('business_id', profile.id)
          .eq('user_id', userId)
        
        if (error) {
          setTeamErrorModalData({
            title: 'Removal Failed',
            message: `Error removing team member: ${error.message}`
          })
          setShowTeamErrorModal(true)
          return
        }
        
        await loadTeamMembers()
      }
    })
    setShowConfirmModal(true)
  }

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showUserMenu && !(event.target as HTMLElement).closest('.user-menu-container')) {
        setShowUserMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showUserMenu])

  const myJobs = jobs.filter(j => j.destination_garage_id === profile?.id)
  const activeJobs = myJobs.filter(j => ['dispatched', 'en_route', 'arrived', 'towing'].includes(j.status))
  const completedJobs = myJobs.filter(j => j.status === 'payment_completed')
  const totalEarnings = completedJobs.reduce((sum, job) => sum + Number(job.total_amount), 0)

  return (
    <div className="dashboard-layout">
      <aside className={`sidebar ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="sidebar-brand">
          <span>Mk</span>ononi
          <button
            className="mobile-close-btn"
            onClick={() => setMobileMenuOpen(false)}
            style={{
              position: 'absolute',
              right: '1rem',
              top: '1.5rem',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--text)',
              padding: '0.5rem'
            }}
          >
            <X size={20} />
          </button>
        </div>
        <nav className="sidebar-nav">
          <div className="sidebar-section">
            <div className="sidebar-section-title">Overview</div>
            <div className={`sidebar-item ${tab === 'overview' ? 'active' : ''}`} onClick={() => setTab('overview')}>
              <LayoutDashboard size={20} />
              <span>Dashboard</span>
            </div>
          </div>
          <div className="sidebar-section">
            <div className="sidebar-section-title">Operations</div>
            <div className={`sidebar-item ${tab === 'jobs' ? 'active' : ''}`} onClick={() => setTab('jobs')}>
              <Truck size={20} />
              <span>Jobs</span>
              {activeJobs.length > 0 && (
                <span style={{
                  marginLeft: 'auto',
                  background: 'var(--red)',
                  color: 'white',
                  padding: '0.125rem 0.5rem',
                  borderRadius: '9999px',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                }}>
                  {activeJobs.length}
                </span>
              )}
            </div>
          </div>
          <div className="sidebar-section">
            <div className="sidebar-section-title">Financial</div>
            <div className={`sidebar-item ${tab === 'earnings' ? 'active' : ''}`} onClick={() => setTab('earnings')}>
              <DollarSign size={20} />
              <span>Earnings</span>
            </div>
            <div className="sidebar-item" onClick={() => navigate('/payments')}>
              <CreditCard size={20} />
              <span>Payments</span>
            </div>
          </div>
          <div className="sidebar-section">
            <div className="sidebar-section-title">Reports</div>
            <div className={`sidebar-item ${tab === 'reports' ? 'active' : ''}`} onClick={() => setTab('reports')}>
              <BarChart size={20} />
              <span>Reports</span>
            </div>
          </div>
          <div className="sidebar-section">
            <div className="sidebar-section-title">Account</div>
            <div className={`sidebar-item ${tab === 'team' ? 'active' : ''}`} onClick={() => setTab('team')}>
              <Users size={20} />
              <span>Team</span>
            </div>
            <div className={`sidebar-item ${tab === 'settings' ? 'active' : ''}`} onClick={() => setTab('settings')}>
              <Settings size={20} />
              <span>Settings</span>
            </div>
          </div>
        </nav>
        <div className="sidebar-bottom-fixed">
          <div className="sidebar-footer">
            <div className="name">{profile?.business_name || 'Garage'}</div>
            <div className="role">garage</div>
            <button className="sign-out" onClick={signOut}>
              <LogOut size={18} />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div
          onClick={() => setMobileMenuOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 99
          }}
          className="mobile-overlay"
        />
      )}

      <main className="dashboard-main">
        <header className="dashboard-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button
              className="mobile-menu-btn"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0.5rem',
                color: 'var(--text)'
              }}
            >
              <Menu size={24} />
            </button>
            <div>
              <h1>
                {tab === 'overview' && 'Garage Dashboard'}
                {tab === 'jobs' && 'Jobs Management'}
                {tab === 'earnings' && 'Earnings'}
                {tab === 'reports' && 'Reports'}
                {tab === 'team' && 'Team Management'}
                {tab === 'settings' && 'Settings'}
              </h1>
              <p className="subtitle">
                Welcome back, {profile?.business_name?.split(' ')[0] || 'Garage'}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexShrink: 0 }}>
            {/* Switch Role Button - Only show if multi-role */}
            {userRoles.length > 1 && (
              <button
                onClick={() => setShowSwitchRoleModal(true)}
                className="btn btn-outline"
                style={{ padding: '0.5rem 1rem', gap: '0.5rem' }}
              >
                <BadgeCheck size={18} />
                <span className="desktop-only" style={{ marginLeft: '0.5rem' }}>Switch Role</span>
              </button>
            )}

            {/* Notifications Button */}
            <button className="btn btn-outline" style={{ padding: '0.5rem' }}>
              <Bell size={18} />
              <span className="desktop-only" style={{ marginLeft: '0.5rem' }}>Notifications</span>
            </button>

            {/* User Menu */}
            <div className="user-menu-container" style={{ position: 'relative' }}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 0.75rem',
                  background: 'white',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                  fontWeight: 600,
                  color: 'var(--text)',
                  fontSize: '0.8rem'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
              >
                <User size={16} />
                <span>{profile?.business_name?.split(' ')[0] || 'Garage'}</span>
                <ChevronDown size={14} />
              </button>
              {showUserMenu && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '0.5rem',
                    background: 'white',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-sm)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    minWidth: '160px',
                    zIndex: 1000
                  }}
                >
                  <button
                    onClick={() => { setTab('settings'); setShowUserMenu(false) }}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      fontSize: '0.875rem',
                      color: 'var(--text)',
                      fontWeight: 500
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                  >
                    <User size={16} />
                    Profile
                  </button>
                  <button
                    onClick={() => { signOut(); setShowUserMenu(false) }}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      fontSize: '0.875rem',
                      color: '#dc2626',
                      fontWeight: 500
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                  >
                    <LogOut size={16} />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="dashboard-content">
          {tab === 'overview' && (
            <>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: 'var(--red-light)', color: 'var(--red)' }}>
                    <Truck size={24} />
                  </div>
                  <div className="stat-label">Active Jobs</div>
                  <div className="stat-value">{activeJobs.length}</div>
                  <div className="stat-trend">
                    <Activity size={14} />
                    Currently in progress
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: 'var(--green-light)', color: 'var(--green)' }}>
                    <CheckCircle size={24} />
                  </div>
                  <div className="stat-label">Completed Jobs</div>
                  <div className="stat-value">{completedJobs.length}</div>
                  <div className="stat-trend positive">
                    <TrendingUp size={14} />
                    Total completed
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: 'var(--amber-light)', color: 'var(--amber)' }}>
                    <CheckCircle size={24} />
                  </div>
                  <div className="stat-label">Completed Jobs</div>
                  <div className="stat-value">{completedJobs.length}</div>
                  <div className="stat-trend">
                    <Activity size={14} />
                    Total completed
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: 'var(--purple-light)', color: 'var(--purple)' }}>
                    <DollarSign size={24} />
                  </div>
                  <div className="stat-label">Total Earnings</div>
                  <div className="stat-value">KES {totalEarnings.toLocaleString()}</div>
                  <div className="stat-trend">
                    <BarChart3 size={14} />
                    All time revenue
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
                <div className="card">
                  <h2 className="card-title">Active Jobs</h2>
                  {activeJobs.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--muted)' }}>
                      <Truck size={32} style={{ margin: '0 auto 0.5rem', opacity: 0.5 }} />
                      <p>No active jobs</p>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      {activeJobs.slice(0, 3).map(job => (
                        <div
                          key={job.id}
                          style={{
                            padding: '1rem',
                            background: 'var(--bg)',
                            borderRadius: 'var(--radius-sm)',
                            border: '1px solid var(--border)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{job.reference}</div>
                            <div style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>
                              {job.service_type.replace('_', ' ')}
                            </div>
                          </div>
                          <span className="badge badge-blue">
                            {job.status.replace('_', ' ')}
                          </span>
                        </div>
                      ))}
                      {activeJobs.length > 3 && (
                        <button
                          onClick={() => setTab('jobs')}
                          className="btn btn-primary btn-sm"
                          style={{ width: '100%' }}
                        >
                          View All ({activeJobs.length})
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <div className="card">
                  <h2 className="card-title">Quick Actions</h2>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                    <button
                      onClick={() => setTab('jobs')}
                      className="btn btn-outline"
                      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', padding: '1.5rem' }}
                    >
                      <Navigation size={24} />
                      <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>View Jobs</span>
                    </button>
                    <button
                      onClick={() => navigate('/payments')}
                      className="btn btn-outline"
                      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', padding: '1.5rem' }}
                    >
                      <CreditCard size={24} />
                      <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Payments</span>
                    </button>
                    <button
                      onClick={() => setTab('earnings')}
                      className="btn btn-outline"
                      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', padding: '1.5rem' }}
                    >
                      <DollarSign size={24} />
                      <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Earnings</span>
                    </button>
                    <button
                      onClick={() => setTab('settings')}
                      className="btn btn-outline"
                      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', padding: '1.5rem' }}
                    >
                      <Settings size={24} />
                      <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Settings</span>
                    </button>
                  </div>
                </div>
              </div>

              <div className="card">
                <h2 className="card-title">Recent Activity</h2>
                {myJobs.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
                    <Activity size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                    <p>No recent activity</p>
                  </div>
                ) : (
                  <div className="data-table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Reference</th>
                        <th>Service Type</th>
                        <th>Status</th>
                        <th>Date</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {myJobs.slice(0, 5).map(job => (
                        <tr key={job.id}>
                          <td style={{ fontWeight: 600 }}>{job.reference}</td>
                          <td>{job.service_type.replace('_', ' ')}</td>
                          <td>
                            <span className={`badge ${
                              job.status === 'payment_completed' ? 'badge-green' :
                              job.status === 'cancelled' ? 'badge-red' :
                              'badge-blue'
                            }`}>
                              {job.status.replace('_', ' ')}
                            </span>
                          </td>
                          <td>{new Date(job.created_at).toLocaleDateString()}</td>
                          <td style={{ fontWeight: 600 }}>
                            KES {Number(job.total_amount).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                )}
              </div>
            </>
          )}

          {tab === 'jobs' && (
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 className="card-title" style={{ fontSize: '1.25rem', color: 'var(--text)' }}>Jobs Management</h2>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button className={`btn btn-sm ${tab === 'jobs' ? 'btn-primary' : 'btn-outline'}`}>
                    All Jobs
                  </button>
                  <button className="btn btn-primary btn-sm">
                    Active ({activeJobs.length})
                  </button>
                  <button className="btn btn-primary btn-sm">
                    Completed ({completedJobs.length})
                  </button>
                </div>
              </div>
              {myJobs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
                  <Truck size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                  <p>No jobs yet</p>
                </div>
              ) : (
                <div className="data-table-wrapper">
                  <table className="data-table">
                  <thead>
                    <tr>
                      <th>Reference</th>
                      <th>Service Type</th>
                      <th>Status</th>
                      <th>Pickup Location</th>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myJobs.map(job => (
                      <tr key={job.id}>
                        <td style={{ fontWeight: 600 }}>{job.reference}</td>
                        <td>{job.service_type.replace('_', ' ')}</td>
                        <td>
                          <span className={`badge ${
                            job.status === 'payment_completed' ? 'badge-green' :
                            job.status === 'cancelled' ? 'badge-red' :
                            'badge-blue'
                          }`}>
                            {job.status.replace('_', ' ')}
                          </span>
                        </td>
                        <td>{job.pickup_address}</td>
                        <td>{new Date(job.created_at).toLocaleDateString()}</td>
                        <td style={{ fontWeight: 600 }}>
                          KES {Number(job.total_amount).toLocaleString()}
                        </td>
                        <td>
                          <button className="btn btn-primary btn-sm">View</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                  </div>
              )}
            </div>
          )}

          {tab === 'earnings' && (
            <>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: 'var(--green-light)', color: 'var(--green)' }}>
                    <DollarSign size={24} />
                  </div>
                  <div className="stat-label">Total Earnings</div>
                  <div className="stat-value">KES {totalEarnings.toLocaleString()}</div>
                  <div className="stat-trend positive">
                    <TrendingUp size={14} />
                    All time earnings
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: 'var(--blue-light)', color: 'var(--blue)' }}>
                    <CheckCircle size={24} />
                  </div>
                  <div className="stat-label">Completed Jobs</div>
                  <div className="stat-value">{completedJobs.length}</div>
                  <div className="stat-trend">
                    <Activity size={14} />
                    Total completed
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: 'var(--amber-light)', color: 'var(--amber)' }}>
                    <Star size={24} />
                  </div>
                  <div className="stat-label">Avg per Job</div>
                  <div className="stat-value">
                    KES {completedJobs.length > 0 ? Math.round(totalEarnings / completedJobs.length).toLocaleString() : '0'}
                  </div>
                  <div className="stat-trend">
                    <BarChart3 size={14} />
                    Average earning
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: 'var(--purple-light)', color: 'var(--purple)' }}>
                    <Clock size={24} />
                  </div>
                  <div className="stat-label">This Week</div>
                  <div className="stat-value">
                    KES {completedJobs
                      .filter(j => {
                        const weekAgo = new Date()
                        weekAgo.setDate(weekAgo.getDate() - 7)
                        return new Date(j.created_at) >= weekAgo
                      })
                      .reduce((sum, j) => sum + Number(j.total_amount), 0)
                      .toLocaleString()}
                  </div>
                  <div className="stat-trend">
                    <Activity size={14} />
                    Last 7 days
                  </div>
                </div>
              </div>

              <div className="card">
                <h2 className="card-title">Earnings History</h2>
                {completedJobs.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
                    <DollarSign size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                    <p>No earnings yet</p>
                  </div>
                ) : (
                  <div className="data-table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Reference</th>
                        <th>Service Type</th>
                        <th>Date</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {completedJobs.map(job => (
                        <tr key={job.id}>
                          <td style={{ fontWeight: 600 }}>{job.reference}</td>
                          <td>{job.service_type.replace('_', ' ')}</td>
                          <td>{new Date(job.created_at).toLocaleDateString()}</td>
                          <td style={{ fontWeight: 600 }}>
                            KES {Number(job.total_amount).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                )}
              </div>
            </>
          )}

          {tab === 'reports' && (
            <div className="card">
              <h2 className="card-title" style={{ fontSize: '1.25rem', color: 'var(--text)', marginBottom: '1.5rem' }}>Garage Reports</h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
                <div style={{ padding: '1.5rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                    <Truck size={24} style={{ color: 'var(--blue)' }} />
                    <div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--muted)', fontWeight: 600 }}>Total Jobs</div>
                      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)' }}>{jobs.length}</div>
                    </div>
                  </div>
                </div>
                <div style={{ padding: '1.5rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                    <CheckCircle size={24} style={{ color: 'var(--green)' }} />
                    <div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--muted)', fontWeight: 600 }}>Completed</div>
                      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)' }}>{jobs.filter(j => j.status === 'completed').length}</div>
                    </div>
                  </div>
                </div>
                <div style={{ padding: '1.5rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                    <DollarSign size={24} style={{ color: 'var(--red)' }} />
                    <div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--muted)', fontWeight: 600 }}>Revenue</div>
                      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)' }}>KES 0</div>
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
                <BarChart3 size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                <div style={{ fontSize: '1rem', fontWeight: 500 }}>More reports coming soon</div>
                <div style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>Detailed analytics and charts will be available here</div>
              </div>
            </div>
          )}

          {tab === 'team' && (
            <div className="card" style={{ marginBottom: '2rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Users size={24} color="white" />
                  </div>
                  <div>
                    <h2 className="card-title" style={{ fontSize: '1.5rem', color: 'var(--text)', marginBottom: '0.25rem' }}>Team Members</h2>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: 0 }}>
                      Manage staff members who can access your dashboard
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowAddTeamMemberModal(true)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                    border: 'none',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 4px rgba(220, 38, 38, 0.2)'
                  }}
                  onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 6px rgba(220, 38, 38, 0.3)' }}
                  onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(220, 38, 38, 0.2)' }}
                >
                  <Plus size={18} />
                  Add Team Member
                </button>
              </div>

              {teamMembers.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
                  <Users size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                  <p>No team members yet</p>
                  <p style={{ fontSize: '0.875rem' }}>Add staff members to help manage your operations</p>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '1rem', marginTop: '1.5rem' }}>
                  {teamMembers.map((member) => (
                    <div
                      key={member.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '1rem',
                        background: 'white',
                        borderRadius: '12px',
                        border: '1px solid var(--border)',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          background: 'var(--bg)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.25rem',
                          fontWeight: 700,
                          color: 'var(--text-secondary)'
                        }}>
                          {member.profiles?.full_name?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, color: 'var(--text)' }}>
                            {member.profiles?.full_name || 'Unknown'}
                          </div>
                          <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                            {member.profiles?.email || 'No email'}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '20px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          background: member.role === 'admin' ? '#fee2e2' : 'var(--bg)',
                          color: member.role === 'admin' ? '#dc2626' : 'var(--text-secondary)'
                        }}>
                          {member.role === 'admin' ? 'Admin' : 'Staff'}
                        </span>
                        <button
                          onClick={() => removeTeamMember(member.user_id)}
                          style={{
                            padding: '0.5rem 0.75rem',
                            borderRadius: '8px',
                            background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                            border: 'none',
                            color: 'white',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            transition: 'all 0.2s',
                            boxShadow: '0 2px 4px rgba(220, 38, 38, 0.2)'
                          }}
                          onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 6px rgba(220, 38, 38, 0.3)' }}
                          onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(220, 38, 38, 0.2)' }}
                        >
                          <Trash2 size={14} />
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === 'settings' && (
            <div className="card">
              <h2 className="card-title" style={{ fontSize: '1.25rem', color: 'var(--text)', marginBottom: '1.5rem' }}>Account Settings</h2>
              <div style={{ maxWidth: '600px' }}>
                <div style={{ marginBottom: '2rem' }}>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>Business Name</label>
                  <input
                    type="text"
                    className="form-input"
                    defaultValue={profile?.business_name}
                    style={{ width: '100%', padding: '0.75rem 1rem', border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)' }}
                  />
                </div>
                <div style={{ marginBottom: '2rem' }}>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>Phone Number</label>
                  <input
                    type="tel"
                    className="form-input"
                    defaultValue={profile?.phone}
                    style={{ width: '100%', padding: '0.75rem 1rem', border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)' }}
                  />
                </div>
                <div style={{ marginBottom: '2rem' }}>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>Email</label>
                  <input
                    type="email"
                    className="form-input"
                    defaultValue={profile?.email || ''}
                    style={{ width: '100%', padding: '0.75rem 1rem', border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)' }}
                  />
                </div>
                <button className="btn btn-primary">Save Changes</button>
              </div>
            </div>
          )}

          {/* Add Team Member Modal */}
          {showAddTeamMemberModal && (
            <div style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}>
              <div style={{
                background: 'white',
                borderRadius: '16px',
                padding: '2rem',
                maxWidth: '500px',
                width: '90%',
                maxHeight: '90vh',
                overflowY: 'auto'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                  <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)', margin: 0 }}>Add Team Member</h2>
                  <button
                    onClick={() => setShowAddTeamMemberModal(false)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem' }}
                  >
                    <X size={24} style={{ color: 'var(--text-secondary)' }} />
                  </button>
                </div>

                <div style={{ display: 'grid', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.5rem' }}>
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={newTeamMember.full_name}
                      onChange={(e) => setNewTeamMember({ ...newTeamMember, full_name: e.target.value })}
                      placeholder="John Doe"
                      style={{
                        width: '100%',
                        padding: '0.875rem',
                        border: '2px solid var(--border)',
                        borderRadius: '0.75rem',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.5rem' }}>
                      Email *
                    </label>
                    <input
                      type="email"
                      value={newTeamMember.email}
                      onChange={(e) => setNewTeamMember({ ...newTeamMember, email: e.target.value })}
                      placeholder="john@example.com"
                      style={{
                        width: '100%',
                        padding: '0.875rem',
                        border: '2px solid var(--border)',
                        borderRadius: '0.75rem',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.5rem' }}>
                      Role *
                    </label>
                    <select
                      value={newTeamMember.role}
                      onChange={(e) => setNewTeamMember({ ...newTeamMember, role: e.target.value as 'admin' | 'staff' })}
                      style={{
                        width: '100%',
                        padding: '0.875rem',
                        border: '2px solid var(--border)',
                        borderRadius: '0.75rem',
                        fontSize: '1rem',
                        background: 'white'
                      }}
                    >
                      <option value="staff">Staff - Full access to dashboard</option>
                      <option value="admin">Admin - Full access + can manage team</option>
                    </select>
                  </div>

                  <div style={{
                    padding: '1rem',
                    background: 'var(--bg)',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    color: 'var(--text-secondary)'
                  }}>
                    <strong>Note:</strong> The team member will be created with a strong temporary password (12+ characters with uppercase, lowercase, numbers, and special characters). They should change it after first login.
                  </div>

                  <div style={{ display: 'flex', gap: '0.75rem', marginTop: '2rem' }}>
                    <button
                      onClick={() => {
                        setShowAddTeamMemberModal(false)
                        setNewTeamMember({ email: '', full_name: '', role: 'staff' })
                      }}
                      style={{
                        flex: 1,
                        padding: '0.75rem 1rem',
                        background: 'white',
                        border: '2px solid var(--border)',
                        borderRadius: 'var(--radius-sm)',
                        color: 'var(--text)',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={addTeamMember}
                      disabled={addingTeamMember || !newTeamMember.email || !newTeamMember.full_name}
                      style={{
                        flex: 1,
                        padding: '0.75rem 1rem',
                        background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                        border: 'none',
                        borderRadius: 'var(--radius-sm)',
                        color: 'white',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        cursor: addingTeamMember ? 'not-allowed' : 'pointer',
                        opacity: addingTeamMember ? 0.6 : 1
                      }}
                    >
                      {addingTeamMember ? 'Adding...' : 'Add Team Member'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Team Success Modal */}
      <TeamMemberSuccessModal
        isOpen={showTeamSuccessModal}
        onClose={() => setShowTeamSuccessModal(false)}
        memberData={teamSuccessModalData}
        emailSent={teamSuccessModalData.emailSent}
      />

      {/* Team Error Modal */}
      <ErrorModal
        isOpen={showTeamErrorModal}
        onClose={() => setShowTeamErrorModal(false)}
        title={teamErrorModalData.title}
        message={teamErrorModalData.message}
      />

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={confirmModalData.onConfirm}
        title={confirmModalData.title}
        message={confirmModalData.message}
        confirmText="Remove"
        cancelText="Cancel"
      />

      {/* Switch Role Modal */}
      {showSwitchRoleModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            maxWidth: '400px',
            width: '90%'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
              <h2 style={{ fontSize: '1.1rem', fontWeight: 600, color: 'var(--text)' }}>
                Switch Role
              </h2>
              <button
                onClick={() => setShowSwitchRoleModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem'
                }}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ display: 'grid', gap: '0.5rem', marginBottom: '1rem' }}>
              {userRoles.map((role) => {
                const config = availableRoles.find(r => r.value === role)
                const Icon = config?.icon || Wrench
                const isCurrent = role === 'garage'
                return (
                  <button
                    key={role}
                    onClick={() => {
                      if (role !== 'garage') {
                        localStorage.setItem('preferredRole', role)
                        window.location.reload()
                      }
                    }}
                    disabled={isCurrent}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '0.75rem',
                      borderRadius: '0.375rem',
                      border: isCurrent ? '2px solid var(--red)' : '1px solid var(--border)',
                      background: isCurrent ? 'rgba(239, 68, 68, 0.05)' : 'white',
                      cursor: isCurrent ? 'not-allowed' : 'pointer',
                      opacity: isCurrent ? 0.7 : 1,
                      transition: 'all 0.2s',
                      textAlign: 'left'
                    }}
                  >
                    <Icon size={18} style={{ color: isCurrent ? 'var(--red)' : 'var(--text)' }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)' }}>
                        {role.charAt(0).toUpperCase() + role.slice(1)}
                      </div>
                    </div>
                    {isCurrent && (
                      <span style={{
                        padding: '0.125rem 0.375rem',
                        background: 'var(--red)',
                        color: 'white',
                        borderRadius: '0.25rem',
                        fontSize: '0.7rem',
                        fontWeight: 500
                      }}>
                        Current
                      </span>
                    )}
                  </button>
                )
              })}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowSwitchRoleModal(false)}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '0.375rem',
                  border: '1px solid var(--border)',
                  background: 'white',
                  color: 'var(--text)',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
